"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { api } from "@/convex/_generated/api";
import { useAction, useQuery } from "convex/react";
import { Row } from "../../_components/row/Row";
import { Text } from "../../_components/text/Text";
import { Column } from "../../_components/column/Column";
import { Loader2, Check, Clock, Users } from "lucide-react";
import { useCrmStatus } from "@/app/_hooks/use-crm-status";

// CRMs where we run an "activation sync" (direct API fetch into Convex) so the
// onboarding enrichment can proceed immediately. For these, we MUST NOT fall
// back to Postgres polling — we wait for the activationSyncStatus doc.
// Keep in sync with APP_KEY_TO_PROVIDER in convex/activationSync/actions.ts.
const ACTIVATION_SYNC_APP_KEYS = new Set(["lofty", "follow-up-boss", "boldtrail"]);

// ---------------------------------------------------------------------------
// Sync phases (for the Rollout-Postgres fallback path)
// ---------------------------------------------------------------------------

type SyncPhase = "polling" | "first-detected" | "threshold-reached" | "abandoned";

const POLL_INTERVAL_MS = 4000;
const CONTACT_THRESHOLD = 1000;
const THRESHOLD_TIMEOUT_MS = 10000; // 10s after first contact
const ABANDON_TIMEOUT_MS = 30000; // 30s with 0 contacts

// ---------------------------------------------------------------------------
// Animated status messages
// ---------------------------------------------------------------------------

const STATUS_MESSAGES = [
  { after: 0, text: "Connecting to your CRM..." },
  { after: 3000, text: "Initializing API request..." },
  { after: 8000, text: "Requesting contacts..." },
  { after: 15000, text: "Syncing your contacts... this can take a minute" },
  { after: 25000, text: "Almost there..." },
];

function getStatusMessage(elapsedMs: number): string {
  let msg = STATUS_MESSAGES[0].text;
  for (const s of STATUS_MESSAGES) {
    if (elapsedMs >= s.after) msg = s.text;
  }
  return msg;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ContactSyncStatusProps {
  onReady: (contactCount: number) => void;
  crmName?: string;
}

export function ContactSyncStatus({ onReady, crmName }: ContactSyncStatusProps) {
  // Subscribe to the activationSyncStatus doc (Lofty onboarding path). This
  // wins over the Postgres-count polling when an activation sync is running.
  const activationStatus = useQuery(api.activationSync.queries.getStatusForCurrentTeam, {});

  // Read the team's CRM so we can decide whether the Postgres fallback is even
  // valid. For CRMs that have an activation sync (currently just Lofty), we
  // never fall back — we wait for the status doc, however long that takes.
  const crmStatus = useCrmStatus();
  const usesActivationSync = crmStatus.appKey ? ACTIVATION_SYNC_APP_KEYS.has(crmStatus.appKey) : false;

  const countContacts = useAction(api.rolloutPostgres.publicQueries.countRolloutContacts);
  const startActivationSync = useAction(api.activationSync.actions.startActivationSync);

  const [phase, setPhase] = useState<SyncPhase>("polling");
  const [contactCount, setContactCount] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const startTimeRef = useRef(Date.now());
  const firstDetectedAtRef = useRef<number | null>(null);
  const hasTriggeredRef = useRef(false);
  // Set once we've fired the fallback `startActivationSync` so we don't spam
  // the action while waiting for the status doc to appear.
  const fallbackFiredRef = useRef(false);
  // Mirror `phase` into a ref so the polling useEffect can early-exit
  // without including `phase` in its dependency array. Including it there
  // would tear down and recreate the interval on every phase change, which
  // can fire overlapping polls during the first-detected → threshold handoff.
  const phaseRef = useRef<SyncPhase>("polling");

  // We've resolved the question "is there an activation sync for this team?"
  // once the Convex query returns (undefined = still loading).
  // For activation-sync CRMs (Lofty), we treat the path as "active" even while
  // the doc is still being created — that prevents a brief flicker into the
  // Postgres polling UI during the action's startup window.
  const hasActivationSync = usesActivationSync || (activationStatus !== null && activationStatus !== undefined);
  // When we know there's no activation sync AND this CRM doesn't use one, fall
  // back to the Postgres poller. Lofty (and other activation-sync CRMs) NEVER
  // fall back — Rollout's background sync is too slow to be useful here.
  const shouldPollRollout = !usesActivationSync && activationStatus === null;

  // Tick elapsed time for status messages
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 500);
    return () => clearInterval(timer);
  }, []);

  // Handle threshold-reached → trigger onReady
  const handleThresholdReached = useCallback(
    (count: number) => {
      if (hasTriggeredRef.current) return;
      hasTriggeredRef.current = true;
      phaseRef.current = "threshold-reached";
      setPhase("threshold-reached");
      onReady(count);
    },
    [onReady],
  );

  // Fallback trigger: if the team uses an activation-sync CRM but no status
  // doc has appeared after a brief delay, the connect-flow trigger probably
  // didn't fire (e.g. browser hadn't HMR'd the latest constants, or the user
  // landed here by some path other than `createCredential`). Kick off
  // `startActivationSync` ourselves — it reads the apiKey from
  // `team.crm.credentials.apiKey` (persisted by `connectRollout`).
  useEffect(() => {
    if (!usesActivationSync) return;
    if (activationStatus !== null) return; // doc exists OR query still loading
    if (fallbackFiredRef.current) return;

    const timer = setTimeout(() => {
      fallbackFiredRef.current = true;
      void startActivationSync({}).catch((err) => {
        console.error("[ContactSyncStatus] Fallback startActivationSync failed:", err);
      });
    }, 4000);

    return () => clearTimeout(timer);
  }, [usesActivationSync, activationStatus, startActivationSync]);

  // Activation-sync path: trigger enrichment once the status doc flips to "complete"
  // (or when we've fetched enough contacts and are still in "fetching").
  useEffect(() => {
    if (!activationStatus) return;
    if (hasTriggeredRef.current) return;

    if (activationStatus.status === "complete") {
      handleThresholdReached(activationStatus.totalFetched);
      return;
    }

    // If Lofty has fewer leads than our target and we've already pulled all of them,
    // don't wait — kick off enrichment now. (status stays "fetching" briefly before the
    // action flips it to "complete".)
    if (
      activationStatus.status === "fetching" &&
      activationStatus.totalAvailable !== undefined &&
      activationStatus.totalFetched >= activationStatus.totalAvailable &&
      activationStatus.totalFetched > 0
    ) {
      handleThresholdReached(activationStatus.totalFetched);
    }
  }, [activationStatus, handleThresholdReached]);

  // Rollout-Postgres fallback polling path (only when we've confirmed there's no activation sync).
  // Stops polling once we've either triggered enrichment OR given up (abandoned) via a ref check —
  // keeping `phase` out of deps means the interval isn't torn down mid-tick on phase changes.
  useEffect(() => {
    if (!shouldPollRollout) return;

    const poll = async () => {
      if (phaseRef.current === "threshold-reached" || phaseRef.current === "abandoned") return;

      try {
        const result = await countContacts({});
        const count = result.total;
        setContactCount(count);

        if (count > 0 && firstDetectedAtRef.current === null) {
          firstDetectedAtRef.current = Date.now();
          phaseRef.current = "first-detected";
          setPhase("first-detected");
        }

        if (count >= CONTACT_THRESHOLD) {
          handleThresholdReached(count);
          return;
        }

        if (firstDetectedAtRef.current !== null) {
          const sinceFirst = Date.now() - firstDetectedAtRef.current;
          if (sinceFirst >= THRESHOLD_TIMEOUT_MS) {
            handleThresholdReached(count);
            return;
          }
        }

        if (count === 0) {
          const elapsed = Date.now() - startTimeRef.current;
          if (elapsed >= ABANDON_TIMEOUT_MS) {
            phaseRef.current = "abandoned";
            setPhase("abandoned");
          }
        }
      } catch (err) {
        console.error("[ContactSyncStatus] Poll failed:", err);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [shouldPollRollout, countContacts, handleThresholdReached]);

  // Rollout DB-sync callback: the authoritative gate signal. When Rollout
  // confirms the credential's initial sync to Postgres, open the gate
  // immediately instead of waiting for the contact-count heuristic.
  useEffect(() => {
    if (!shouldPollRollout) return;
    if (hasTriggeredRef.current) return;
    if (!crmStatus.rolloutSync?.dbSyncedAt) return;

    let cancelled = false;
    void (async () => {
      let count = 0;
      try {
        const result = await countContacts({});
        count = result.total;
      } catch (err) {
        console.error("[ContactSyncStatus] db-synced count fetch failed:", err);
      }
      if (cancelled) return;
      setContactCount(count);
      handleThresholdReached(count);
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldPollRollout, crmStatus.rolloutSync?.dbSyncedAt, countContacts, handleThresholdReached]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const displayName = crmName || "CRM";

  // --- Activation-sync path rendering ---
  if (hasActivationSync) {
    // Loading window: the team uses an activation-sync CRM (Lofty) but the
    // status doc hasn't been created yet. Show a generic "starting up" state
    // instead of the Postgres polling UI — we don't want users to ever see
    // that path on Lofty.
    if (!activationStatus) {
      return (
        <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-5">
          <Row align className="gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
            <Column noGap>
              <Text fat className="text-blue-900">
                Connecting to {displayName}...
              </Text>
              <Text sm className="text-blue-700">
                Pulling your contacts directly from {displayName} so we can start enrichment right away.
              </Text>
            </Column>
          </Row>
        </div>
      );
    }

    const { status, totalFetched, targetCount, totalAvailable, error } = activationStatus;
    const cap = totalAvailable !== undefined ? Math.min(totalAvailable, targetCount) : targetCount;
    const pct = cap > 0 ? Math.min(100, Math.round((totalFetched / cap) * 100)) : 0;

    if (status === "failed") {
      return (
        <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-5">
          <Row align className="gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-rose-600" />
            </div>
            <Column noGap>
              <Text fat className="text-rose-900">
                Contact sync failed
              </Text>
              <Text sm className="text-rose-700">
                {error ?? `We couldn't fetch contacts from ${displayName}. Please reconnect and try again.`}
              </Text>
            </Column>
          </Row>
        </div>
      );
    }

    if (status === "complete") {
      return (
        <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-5">
          <Row align className="gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-teal-600" />
            </div>
            <Column noGap>
              <Text fat className="text-teal-900">
                {totalFetched.toLocaleString()} contacts synced
              </Text>
              <Text sm className="text-teal-700">
                Starting enrichment...
              </Text>
            </Column>
          </Row>
        </div>
      );
    }

    // pending / fetching
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-5">
        <Row align className="gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            {totalFetched > 0 ? (
              <Users className="w-5 h-5 text-blue-600" />
            ) : (
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            )}
          </div>
          <Column noGap>
            <Text fat className="text-blue-900">
              {totalFetched > 0
                ? `Fetched ${totalFetched.toLocaleString()} of ${cap.toLocaleString()} contacts`
                : `Fetching contacts from ${displayName}...`}
            </Text>
            <Text sm className="text-blue-700">
              Pulling your contacts directly from {displayName} so we can start enrichment right away.
            </Text>
          </Column>
        </Row>

        {/* Progress bar */}
        <div className="mt-4 pl-[52px]">
          <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>
    );
  }

  // --- Rollout-Postgres fallback rendering (unchanged) ---

  if (phase === "threshold-reached") {
    return (
      <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-5">
        <Row align className="gap-3">
          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-teal-600" />
          </div>
          <Column noGap>
            <Text fat className="text-teal-900">
              {contactCount.toLocaleString()} contacts synced
            </Text>
            <Text sm className="text-teal-700">
              Starting enrichment...
            </Text>
          </Column>
        </Row>
      </div>
    );
  }

  if (crmStatus.rolloutSync?.erroredAt && !crmStatus.rolloutSync?.dbSyncedAt) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-5">
        <Row align className="gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-rose-600" />
          </div>
          <Column noGap>
            <Text fat className="text-rose-900">
              Contact sync failed
            </Text>
            <Text sm className="text-rose-700">
              {crmStatus.rolloutSync.error ??
                `We couldn't sync your ${displayName} data. Please reconnect and try again.`}
            </Text>
          </Column>
        </Row>
      </div>
    );
  }

  if (phase === "abandoned") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-5">
        <Column className="gap-3">
          <Row align className="gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <Column noGap>
              <Text fat className="text-amber-900">
                Initial sync is going to take a few minutes – let's keep going.
              </Text>
              <Text sm>
                Your {displayName} contacts are still syncing. This can sometimes take several minutes for larger
                databases.
              </Text>
            </Column>
          </Row>
          <Column tightGap className="pl-[52px]">
            <Text sm>
              Let's continue setting up. Come back to this page when your database is ready to enrich — we'll pick
              up right where you left off.
            </Text>
          </Column>
        </Column>
      </div>
    );
  }

  // polling or first-detected
  const message =
    phase === "first-detected"
      ? `Found ${contactCount.toLocaleString()} contact${contactCount === 1 ? "" : "s"} so far, syncing more...`
      : getStatusMessage(elapsedMs);

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-5">
      <Row align className="gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
          {phase === "first-detected" ? (
            <Users className="w-5 h-5 text-blue-600" />
          ) : (
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          )}
        </div>
        <Column noGap>
          <AnimatePresence mode="wait">
            <motion.div
              key={message}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <Text fat className="text-blue-900">
                {message}
              </Text>
            </motion.div>
          </AnimatePresence>
          <Text sm className="text-blue-700">
            {phase === "first-detected"
              ? "Waiting for initial batch before starting enrichment..."
              : `Connecting to ${displayName} and importing your contacts`}
          </Text>
        </Column>
      </Row>

      {/* Subtle progress dots */}
      <div className="flex items-center gap-1.5 mt-4 pl-[52px]">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-blue-400"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
      </div>
    </div>
  );
}
